/**
 * LearningTimeline — the merged learning timeline of one Digital Employee
 * (DIGEMP-LEARNING-CENTER-FRONTEND-054).
 *
 * One chronological stream built only from records that already exist and are already audited:
 *  - outcome   : terminal-run entries from GET /api/agents/:id/experience-history (verified
 *                success / unverified / failed / cancelled — only technician-verified work is
 *                presented as a positive);
 *  - feedback  : technician corrections, rendered inside the outcome entry they correct (the
 *                backend attaches each lesson to its own run — never duplicated as its own item);
 *  - knowledge : open/acknowledged KnowledgeGap rows linked to this employee's own runs
 *                (growth profile "learning goals");
 *  - change    : this employee's own rows from the existing activity log (agent_updated and
 *                friends) — configuration changes an operator can set back.
 *  - skill     : the versioned skill/learning history carried on the agent detail response
 *                (`learning_history`, the evidence-backed proficiency projection): append-only
 *                state changes with their own version, from/to state, reason, evidence and
 *                provenance ids — so any proficiency movement (including a regression or a
 *                suspension) traces back to what actually caused it.
 *
 * Nothing here is derived from model output, prompts, run context or raw payloads: only the
 * bounded scalar fields the backend already exposes are rendered, so secrets/connection material
 * can not reach the screen. The component writes nothing and grants nothing, and the skill
 * history grants nothing by construction: it is the projection's own versioned walk, where a
 * promotion requires verified outcomes and a declared/self claim is never a state change.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

export const OUTCOME_META = {
  verified_success: { icon: "✓", label: "מאומת",    cls: "bg-green-100 text-green-700" },
  unverified:       { icon: "◷", label: "טרם אומת", cls: "bg-amber-50 text-amber-700 border border-amber-200" },
  failed:           { icon: "✕", label: "נכשל",     cls: "bg-red-100 text-red-700" },
  cancelled:        { icon: "⊘", label: "בוטל",     cls: "bg-slate-200 text-slate-700" },
};

export const VERIFICATION_BASIS_HE = {
  technician_resolution_outcome: "אימות: רשומת טיפול שכתב טכנאי",
  approved_close_executed: "אימות: סגירת קריאה מאושרת שבוצעה",
};

export const LESSON_KIND_HE = {
  verified_resolution: "יישוב מאומת",
  technician_correction: "תיקון טכנאי",
  failure_note: "הערת כשל",
  rejection_note: "הערת דחייה",
};

export const FIELD_LABEL_HE = {
  name: "שם", role_title: "תפקיד", description: "תיאור", specialties: "התמחויות",
  system_prompt: "הנחיות מערכת", permission_tier: "רמת הרשאה", autonomy_level: "רמת אוטונומיה",
  allowed_tools: "רשימת כלים מותרים", is_enabled: "מצב הפעלה", model: "מודל",
  availability_status: "זמינות", client_id: "שיוך ללקוח", intro_text: "טקסט פתיחה",
  personality: "אופי", avatar_key: "אווטאר", avatar_color: "צבע אווטאר",
};

export const CHANGE_ACTION_HE = {
  agent_created: "העובד נוצר",
  agent_disabled: "העובד הושבת (השהיה)",
  agent_connection_added: "חיבור נוסף לעובד",
  agent_connection_removed: "חיבור הוסר מהעובד",
};

export const KNOWLEDGE_STATUS_HE = { open: "פתוח", acknowledged: "בטיפול" };

// ─── Versioned skill history vocabulary (learning-history projection) ────────

export const SKILL_STATE_HE = {
  not_started: "טרם נלמד",
  learning: "בלמידה",
  proficient: "מיומן",
  regression: "נסיגה",
  suspended: "מושהה",
};

export const SKILL_STATE_CLS = {
  not_started: "bg-gray-100 text-gray-600",
  learning: "bg-blue-100 text-blue-700",
  proficient: "bg-green-100 text-green-700",
  regression: "bg-red-100 text-red-700",
  suspended: "bg-orange-100 text-orange-700",
};

export const SKILL_CHANGE_KIND_HE = {
  evaluation: "הערכה",
  promotion: "קידום",
  regression: "ירידה",
  suspension: "השהיה",
  reinstated: "חזרה לפעילות",
  knowledge: "ידע מאושר",
};

export const SKILL_CHANGE_KIND_CLS = {
  evaluation: "bg-blue-50 text-blue-700 border border-blue-200",
  promotion: "bg-green-100 text-green-700 border border-green-200",
  regression: "bg-red-100 text-red-700 border border-red-200",
  suspension: "bg-orange-100 text-orange-700 border border-orange-200",
  reinstated: "bg-teal-50 text-teal-700 border border-teal-200",
  knowledge: "bg-indigo-50 text-indigo-700 border border-indigo-200",
};

export const SKILL_EVIDENCE_SOURCE_HE = {
  verified_work: "עבודות מאומתות",
  evaluation: "הערכה מהריצות",
  approved_knowledge: "ידע מאושר",
  operator: "מפעיל/טכנאי",
};

// Shown with every skill-history rendering: the projection is documentation and is never
// consulted by any execution path, so the surface must never imply it grants anything.
export const SKILL_HISTORY_AUTHORITY_NOTE =
  "תיעוד בלבד — היסטוריית המיומנות אינה מרחיבה הרשאות הרצה, חיבורים או אישור ואינה משנה את מכסת הכלים של העובד.";

export const SKILL_CHANGE_LOG_MAX = 8;
export const SKILL_EVALUATED_WINDOW = 100;

// The agent-entity actions that represent a change to the employee an operator can set back.
export const REVERSIBLE_CHANGE_ACTIONS = new Set([
  "agent_created", "agent_updated", "agent_disabled",
  "agent_connection_added", "agent_connection_removed",
]);

const FILTERS = [
  { key: "all",       label: "הכל" },
  { key: "outcome",   label: "תוצאות עבודה" },
  { key: "feedback",  label: "עם משוב טכנאי" },
  { key: "knowledge", label: "מועדי ידע" },
  { key: "skill",     label: "גרסאות מיומנות" },
  { key: "change",    label: "שינויים הפיכים" },
];

function boundedText(value, max) {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null;
}

function boundedNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function boundedInt(value, min = 0) {
  return Number.isInteger(value) && value >= min ? value : null;
}

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("he-IL"); } catch { return ""; }
}

function formatAuditValue(fieldName, value) {
  if (value == null || value === "") return "—";
  const raw = String(value);
  if (fieldName === "is_enabled") {
    if (raw === "true") return "מופעל";
    if (raw === "false") return "מושבת";
  }
  return raw.length > 120 ? `${raw.slice(0, 120)}…` : raw;
}

function normalizeLessons(lessons) {
  if (!Array.isArray(lessons)) return [];
  return lessons
    .map((lesson) => ({
      kind: lesson?.kind,
      summary: boundedText(lesson?.summary, 200),
    }))
    .filter((lesson) => lesson.kind);
}

/**
 * One change of the versioned skill history, bounded to scalar fields. Every field stays an
 * explicit null when the server did not report it — the UI never computes a version, state or
 * reason of its own. Returns null for a row with nothing recognizable to show.
 */
export function normalizeSkillChange(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const change = {
    kind: boundedText(raw.kind, 40),
    version: boundedInt(raw.version, 1),
    at: boundedText(raw.at, 40),
    fromState: boundedText(raw.from_state, 40),
    toState: boundedText(raw.to_state, 40),
    reason: boundedText(raw.reason, 240),
    evidence: boundedText(raw.evidence, 240),
    evidenceSource: boundedText(raw.evidence_source, 40),
    actor: boundedText(raw.actor, 120),
    runId: boundedText(raw.run_id, 64),
    ticketId: boundedText(raw.ticket_id, 64),
    ticketNumber: boundedInt(raw.ticket_number, 1),
    knowledgeId: boundedText(raw.knowledge_id, 64),
    activityId: boundedText(raw.activity_id, 64),
    domain: boundedText(raw.domain, 120),
    subdomain: boundedText(raw.subdomain, 120),
  };
  const meaningful =
    change.kind || change.reason || change.at || change.fromState || change.toState || change.version != null;
  return meaningful ? change : null;
}

/**
 * Normalizes the `learning_history` projection (the versioned skill/learning history carried on
 * the agent detail response) into display-ready structures. Returns null when no payload is
 * present at all; a per-field null then means "not supplied by the server" (rendered as an
 * explicit unknown), while an empty array means the server explicitly reported "none". No value
 * is ever computed or invented here.
 */
export function normalizeVersionedSkillHistory(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const summaryRaw = raw.summary && typeof raw.summary === "object" && !Array.isArray(raw.summary) ? raw.summary : null;
  const domainsRaw = Array.isArray(raw.domains) ? raw.domains : null;
  const changesRaw = Array.isArray(raw.evaluated_changes) ? raw.evaluated_changes : null;

  const domains = [];
  for (const node of domainsRaw || []) {
    if (!node || typeof node !== "object" || Array.isArray(node)) continue;
    const name = boundedText(node.domain, 120);
    const subdomains = [];
    for (const sub of Array.isArray(node.subdomains) ? node.subdomains : []) {
      if (!sub || typeof sub !== "object" || Array.isArray(sub)) continue;
      const changes = (Array.isArray(sub.changes) ? sub.changes : []).map(normalizeSkillChange).filter(Boolean);
      const entry = {
        name: boundedText(sub.subdomain, 120) || name,
        domain: boundedText(sub.domain, 120) || name,
        state: boundedText(sub.state, 40),
        evidenceSource: boundedText(sub.evidence_source, 40),
        evidenceCount: boundedInt(sub.evidence_count, 0),
        since: boundedText(sub.since, 40),
        lastEvaluatedAt: boundedText(sub.last_evaluated_at, 40),
        version: boundedInt(sub.version, 0),
        changes,
      };
      if (entry.name || entry.version != null || entry.changes.length > 0) subdomains.push(entry);
    }
    if (name || subdomains.length > 0) domains.push({ name: name || "כללי", subdomains });
  }

  return {
    summary: summaryRaw
      ? {
          domains: boundedInt(summaryRaw.domains, 0),
          proficient: boundedInt(summaryRaw.proficient, 0),
          learning: boundedInt(summaryRaw.learning, 0),
          regression: boundedInt(summaryRaw.regression, 0),
          suspended: boundedInt(summaryRaw.suspended, 0),
          totalChanges: boundedInt(summaryRaw.total_changes, 0),
        }
      : null,
    domains: domainsRaw === null ? null : domains,
    // The cross-domain evaluated-changes window (bounded server-side); null = not supplied.
    changes: changesRaw === null ? null : changesRaw.map(normalizeSkillChange).filter(Boolean),
    authorityNote: boundedText(raw.authority_note, 300),
    generatedAt: boundedText(raw.generated_at, 40),
  };
}

/**
 * Pure merge of the four read models into one chronological entry list (newest first).
 * Exported for tests; the component below only renders what this returns. `skillChanges` is the
 * evaluated-changes window of the versioned skill history (normalizeVersionedSkillHistory's
 * `changes`), or any sibling list of changes in the same normalized shape.
 */
export function buildLearningEntries({ experience, goals, changes, skillChanges } = {}) {
  const entries = [];

  for (const item of Array.isArray(experience?.entries) ? experience.entries : []) {
    const lessons = normalizeLessons(item?.lessons);
    const feedbackCount = boundedNumber(item?.feedback_count);
    entries.push({
      id: item?.entry_id || `run:${item?.provenance?.run_id || entries.length}`,
      category: "outcome",
      at: item?.occurred_at || null,
      outcome: OUTCOME_META[item?.outcome] ? item.outcome : null,
      summary: boundedText(item?.summary, 300),
      ticket: item?.ticket && typeof item.ticket === "object" ? {
        id: item.ticket.id,
        number: item.ticket.ticket_number,
        title: boundedText(item.ticket.title, 200),
        company: boundedText(item.ticket.company_name, 120),
      } : null,
      verification: item?.verification && item.verification.verified ? {
        basis: item.verification.basis,
        evidence: boundedText(item.verification.evidence, 240),
        verifiedAt: item.verification.verified_at || null,
      } : null,
      actions: item?.actions && boundedNumber(item.actions.total) != null ? {
        total: item.actions.total,
        succeeded: boundedNumber(item.actions.succeeded) || 0,
        failed: boundedNumber(item.actions.failed) || 0,
        rejected: boundedNumber(item.actions.rejected) || 0,
      } : null,
      feedbackCount: feedbackCount && feedbackCount > 0 ? feedbackCount : 0,
      lessons,
      hasCorrection: lessons.some((lesson) => lesson.kind === "technician_correction"),
      runId: boundedText(item?.provenance?.run_id, 64),
    });
  }

  for (const goal of Array.isArray(goals) ? goals : []) {
    entries.push({
      id: `goal:${goal?.goal_id ?? entries.length}`,
      category: "knowledge",
      at: goal?.last_seen_at || goal?.first_seen_at || null,
      topic: boundedText(goal?.topic, 240),
      goalStatus: goal?.status,
      occurrenceCount: boundedNumber(goal?.occurrence_count) || 0,
      sourceRunCount: boundedNumber(goal?.source_run_count) || 0,
      firstSeenAt: goal?.first_seen_at || null,
      lastSeenAt: goal?.last_seen_at || null,
    });
  }

  for (const change of Array.isArray(changes) ? changes : []) {
    entries.push({
      id: `change:${change?.id ?? entries.length}`,
      category: "change",
      at: change?.created_at || null,
      action: change?.action,
      fieldName: change?.field_name || null,
      oldValue: change?.old_value ?? null,
      newValue: change?.new_value ?? null,
      details: boundedText(change?.details, 200),
      byUser: boundedText(change?.user_display_name, 120),
    });
  }

  // Versioned skill-history changes (already normalized): each carries its own version, states,
  // reason, evidence and provenance ids so the entry itself answers "why did this change".
  for (const change of Array.isArray(skillChanges) ? skillChanges : []) {
    if (!change || typeof change !== "object") continue;
    const version = boundedInt(change.version, 1);
    entries.push({
      id: `skill:${change.domain || "?"}:${change.subdomain || "?"}:${version ?? entries.length}`,
      category: "skill",
      at: boundedText(change.at, 40),
      kind: boundedText(change.kind, 40),
      version,
      fromState: boundedText(change.fromState, 40),
      toState: boundedText(change.toState, 40),
      reason: boundedText(change.reason, 240),
      evidence: boundedText(change.evidence, 240),
      evidenceSource: boundedText(change.evidenceSource, 40),
      actor: boundedText(change.actor, 120),
      runId: boundedText(change.runId, 64),
      ticketId: boundedText(change.ticketId, 64),
      ticketNumber: boundedInt(change.ticketNumber, 1),
      knowledgeId: boundedText(change.knowledgeId, 64),
      activityId: boundedText(change.activityId, 64),
      domain: boundedText(change.domain, 120),
      subdomain: boundedText(change.subdomain, 120),
    });
  }

  return entries.sort((a, b) => {
    const byTime = String(b.at || "").localeCompare(String(a.at || ""));
    return byTime !== 0 ? byTime : String(b.id).localeCompare(String(a.id));
  });
}

export function matchesFilter(entry, filterKey) {
  if (!filterKey || filterKey === "all") return true;
  if (filterKey === "feedback") return entry.category === "outcome" && entry.hasCorrection;
  return entry.category === filterKey;
}

function OutcomeEntry({ entry }) {
  const meta = OUTCOME_META[entry.outcome];
  return (
    <li className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${meta.cls}`}>
          {meta.icon} {meta.label}
        </span>
        {entry.verification?.basis && (
          <span className="text-[11px] text-teal-700">
            {VERIFICATION_BASIS_HE[entry.verification.basis] || "אומת"}
          </span>
        )}
        {entry.feedbackCount > 0 && (
          <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
            ✎ {entry.feedbackCount} רשומות משוב
          </span>
        )}
        <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">{fmt(entry.at)}</span>
      </div>

      <p className="text-sm text-gray-800">{entry.summary || "עבודה שהסתיימה (ללא סיכום מתועד)"}</p>

      {entry.verification?.evidence && (
        <p className="text-xs text-teal-800 bg-teal-50/60 border border-teal-100 rounded px-2 py-1">
          ראיות אימות: {entry.verification.evidence}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs">
        {entry.ticket?.id && (
          <Link
            to={`/tickets/${entry.ticket.id}`}
            className="text-blue-600 hover:underline"
            aria-label={`פתח את הקריאה${entry.ticket.number ? ` #${entry.ticket.number}` : ""} שממנה הגיעה העבודה`}
          >
            {entry.ticket.number ? `קריאה #${entry.ticket.number}` : "קריאה מקושרת"}
          </Link>
        )}
        {entry.runId && (
          <Link
            to={`/agent-runs/${entry.runId}`}
            className="text-blue-600 hover:underline"
            aria-label="פתח את הריצה של העבודה הזו"
          >
            צפה בריצה
          </Link>
        )}
        {entry.actions?.total > 0 && (
          <span className="text-gray-500">
            {entry.actions.total} פעולות: {entry.actions.succeeded} הצליחו · {entry.actions.failed} נכשלו · {entry.actions.rejected} נדחו
          </span>
        )}
      </div>

      {entry.lessons.length > 0 && (
        <div className="text-xs text-gray-600 bg-amber-50/70 border border-amber-100 rounded px-2 py-1 space-y-0.5">
          <span className="font-medium text-amber-800">לקחים מתועדים מהעבודה:</span>
          <ul className="space-y-0.5">
            {entry.lessons.map((lesson, i) => (
              <li key={`${lesson.kind}-${i}`}>
                <span className="font-medium">{LESSON_KIND_HE[lesson.kind] || lesson.kind}</span>
                {lesson.summary ? ` — ${lesson.summary}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

function KnowledgeEntry({ entry }) {
  return (
    <li className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] px-1.5 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-700">
          ◆ מועמד ידע
        </span>
        <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-white text-indigo-700 border border-indigo-200">
          {KNOWLEDGE_STATUS_HE[entry.goalStatus] || entry.goalStatus}
        </span>
        <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">{fmt(entry.at)}</span>
      </div>
      <p className="text-sm text-gray-800">{entry.topic || "נושא לא זוהה"}</p>
      <p className="text-xs text-gray-500">
        נצפה {entry.occurrenceCount} פעמים · מבוסס {entry.sourceRunCount} ריצות של העובד
        {entry.firstSeenAt ? ` · נראה לראשונה ${fmt(entry.firstSeenAt)}` : ""}
      </p>
    </li>
  );
}

function ChangeEntry({ entry, agentId }) {
  const fieldLabel = entry.fieldName ? (FIELD_LABEL_HE[entry.fieldName] || entry.fieldName) : null;
  return (
    <li className="rounded-lg border border-gray-200 bg-white p-3 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] px-1.5 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600">
          ↺ שינוי
        </span>
        <span className="text-sm font-medium text-gray-700">
          {entry.action === "agent_updated"
            ? `עודכן שדה: ${fieldLabel || "לא ידוע"}`
            : (CHANGE_ACTION_HE[entry.action] || entry.action)}
        </span>
        {entry.byUser && <span className="text-[11px] text-gray-500">על ידי {entry.byUser}</span>}
        <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">{fmt(entry.at)}</span>
      </div>

      {entry.action === "agent_updated" ? (
        <p className="text-xs text-gray-600">
          מ־<span className="font-medium">{formatAuditValue(entry.fieldName, entry.oldValue)}</span>
          {" "}ל־<span className="font-medium">{formatAuditValue(entry.fieldName, entry.newValue)}</span>
        </p>
      ) : (
        entry.details && <p className="text-xs text-gray-600">{entry.details}</p>
      )}

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-[11px] text-gray-400">ניתן להשיב את המצב הקודם דרך עריכת העובד</span>
        {agentId && (
          <Link to={`/agents/${agentId}`} className="text-blue-600 hover:underline">
            לעריכת העובד
          </Link>
        )}
      </div>
    </li>
  );
}

/**
 * One versioned skill-history change as an operator-facing card: what changed (kind, version,
 * from→to), on which domain, on what evidence, why, and the provenance that carries the trail
 * (run / ticket / approved knowledge / operator decision). Display only; it links to existing
 * read surfaces and offers no action.
 */
export function SkillChangeEntry({ change }) {
  if (!change || typeof change !== "object") return null;
  const kindLabel = SKILL_CHANGE_KIND_HE[change.kind] || change.kind || "שינוי מיומנות";
  const kindCls = SKILL_CHANGE_KIND_CLS[change.kind] || "bg-gray-100 text-gray-700 border border-gray-200";
  const fromLabel = change.fromState ? (SKILL_STATE_HE[change.fromState] || change.fromState) : null;
  const toLabel = change.toState ? (SKILL_STATE_HE[change.toState] || change.toState) : null;
  const sourceLabel = change.evidenceSource
    ? (SKILL_EVIDENCE_SOURCE_HE[change.evidenceSource] || change.evidenceSource)
    : null;
  const hasProvenance = Boolean(
    change.actor || change.runId || change.ticketId || change.knowledgeId || change.activityId,
  );
  return (
    <li className="rounded-lg border border-gray-100 bg-white p-2.5 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${kindCls}`}>
          {kindLabel}
        </span>
        <span
          className={`text-[11px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
            change.version != null
              ? "bg-violet-50 text-violet-700 border border-violet-200"
              : "bg-gray-50 text-gray-500 border border-dashed border-gray-300"
          }`}
        >
          {change.version != null ? `גרסה ${change.version}` : "גרסה לא דווחה"}
        </span>
        {(fromLabel || toLabel) && (
          <span className="text-[11px] text-gray-600 whitespace-nowrap">
            {fromLabel || "—"} → {toLabel || "—"}
          </span>
        )}
        {(change.domain || change.subdomain) && (
          <span className="text-[11px] text-gray-500 whitespace-nowrap">{change.subdomain || change.domain}</span>
        )}
        {sourceLabel && (
          <span className="text-[11px] text-gray-500 whitespace-nowrap">מקור ראיה: {sourceLabel}</span>
        )}
        <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">
          {change.at ? fmt(change.at) : "מועד לא דווח"}
        </span>
      </div>
      {change.reason && <p className="text-xs text-gray-700">{change.reason}</p>}
      {change.evidence && <p className="text-xs text-gray-500">ראיה: {change.evidence}</p>}
      {hasProvenance && (
        <div className="flex flex-wrap items-center gap-3 text-[11px]">
          {change.actor && (
            <span className="text-gray-500">
              מאשר: <span dir="ltr">{change.actor}</span>
            </span>
          )}
          {change.runId && (
            <Link
              to={`/agent-runs/${change.runId}`}
              className="text-blue-600 hover:underline"
              aria-label="פתח את הריצה שממנה נרשמה הראיה של שינוי המיומנות"
            >
              צפה בריצה
            </Link>
          )}
          {change.ticketId && (
            <Link
              to={`/tickets/${change.ticketId}`}
              className="text-blue-600 hover:underline"
              aria-label="פתח את הקריאה שממנה נרשמה הראיה של שינוי המיומנות"
            >
              {change.ticketNumber ? `קריאה #${change.ticketNumber}` : "קריאה מקושרת"}
            </Link>
          )}
          {change.knowledgeId && (
            <span className="text-gray-500">
              ידע מאושר:{" "}
              <Link to="/knowledge" className="text-blue-600 hover:underline">
                ספריית הידע
              </Link>{" "}
              <span dir="ltr" className="font-mono">{change.knowledgeId}</span>
            </span>
          )}
          {change.activityId && (
            <Link
              to="/activity-log"
              className="text-blue-600 hover:underline"
              aria-label="פתח את יומן הפעילות שבו תועדה ההשהיה או ההפעלה מחדש"
            >
              יומן הפעילות
            </Link>
          )}
        </div>
      )}
    </li>
  );
}

function SummaryChip({ label, value }) {
  return (
    <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
      {label}: <span className="font-semibold">{value}</span>
    </span>
  );
}

/**
 * The versioned skill/learning history of one employee, carried by the `learning_history`
 * projection on the agent detail response (raw payload in, normalized here — the same contract
 * LearningProficiencyHistory reads on the workspace surface). Read-only by construction: it
 * renders the projection's own bounded scalar fields and links to existing audited surfaces,
 * and it never implies that a state, version or change grants any permission.
 *
 * Degradation is explicit: a failed projection, a still-loading source and a payload that was
 * never supplied are three distinct states, and a null count is never rendered as 0.
 */
export function SkillVersionHistorySection({ history, isLoading = false, isError = false }) {
  const data = normalizeVersionedSkillHistory(history);
  const summary = data?.summary || null;
  return (
    <section aria-label="היסטוריית גרסאות מיומנות" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-700">היסטוריית גרסאות מיומנות</h2>
        {data?.generatedAt && <span className="text-[11px] text-gray-400">נטען: {fmt(data.generatedAt)}</span>}
      </div>
      <p className="text-[11px] text-gray-400">
        יומן גרסאות שנצבר מראיות מתועדות בלבד — עבודות מאומתות, הערכות מהריצות וידע מאושר;
        הצהרות העובד (מוצהר) אינן מופיעות כאן ואינן מקדמות מצב. לכל שינוי נשמרים הגרסה, המצב
        הקודם והחדש, הסיבה, הראיה והמקור שלה — כך שאפשר לעקוב מדוע מצב המיומנות השתנה. יומן
        מושהה או נסיגה אינו מוחק היסטוריה: הוא מוסיף רשומה.
      </p>
      <p className="text-[11px] text-gray-400">{data?.authorityNote || SKILL_HISTORY_AUTHORITY_NOTE}</p>

      {isError && (
        <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
          לא ניתן לטעון את היסטוריית גרסאות המיומנות — יתר המסך אינו תלוי בה
        </p>
      )}
      {!isError && isLoading && !data && (
        <p className="text-sm text-gray-400 italic">טוען את היסטוריית המיומנות...</p>
      )}
      {!isError && !isLoading && !data && (
        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
          לא ידוע — היסטוריית גרסאות המיומנות לא סופקה על-ידי המערכת; אין להסיק מכך שלא תועדו שינויים.
        </p>
      )}

      {!isError && data && (
        <>
          {summary ? (
            <div className="flex flex-wrap gap-1.5">
              {[
                { label: "תחומים", value: summary.domains },
                { label: "מיומן", value: summary.proficient },
                { label: "בלמידה", value: summary.learning },
                { label: "נסיגה", value: summary.regression },
                { label: "מושהה", value: summary.suspended },
                { label: 'סה"כ שינויים', value: summary.totalChanges },
              ].map(({ label, value }) => (
                <SummaryChip key={label} label={label} value={value == null ? "—" : value} />
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-gray-400">תקציר ההיסטוריה לא סופק על-ידי המערכת.</p>
          )}

          {summary && (summary.regression > 0 || summary.suspended > 0) && (
            <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1">
              {summary.regression > 0 && `${summary.regression} תחומים במצב נסיגה`}
              {summary.regression > 0 && summary.suspended > 0 && " · "}
              {summary.suspended > 0 && `${summary.suspended} תחומים מושהים`}
              {" — "}הנסיגה או ההשהיה נרשמו ביומן עם הסיבה והראיה שלהן, וההיסטוריה נשמרת במלואה.
            </p>
          )}

          {data.domains === null ? (
            <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
              פירוט המצב לפי תחום ותת-תחום לא סופק על-ידי המערכת.
            </p>
          ) : data.domains.length === 0 ? (
            <p className="text-sm text-gray-400 italic">לא דווחו תחומי מיומנות לעובד זה.</p>
          ) : (
            <div className="space-y-3">
              {data.domains.map((domain, i) => (
                <div key={`${domain.name}-${i}`} className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-2">
                  <h3 className="text-xs font-semibold text-gray-600">{domain.name}</h3>
                  {domain.subdomains.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">לא דווחו תתי-תחומים לתחום זה.</p>
                  ) : (
                    <ul className="space-y-2">
                      {domain.subdomains.map((sub, j) => {
                        const stateLabel = sub.state ? (SKILL_STATE_HE[sub.state] || sub.state) : null;
                        const stateCls = SKILL_STATE_CLS[sub.state] || "bg-gray-100 text-gray-500";
                        const sourceLabel = sub.evidenceSource
                          ? (SKILL_EVIDENCE_SOURCE_HE[sub.evidenceSource] || sub.evidenceSource)
                          : null;
                        const shown = sub.changes.slice(-SKILL_CHANGE_LOG_MAX).reverse();
                        return (
                          <li key={`${sub.name}-${j}`} className="rounded-lg border border-gray-200 bg-white p-2.5 space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-medium text-gray-800">{sub.name || "תת-תחום ללא שם"}</span>
                              {stateLabel ? (
                                <span className={`text-[11px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${stateCls}`}>
                                  {stateLabel}
                                </span>
                              ) : (
                                <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-50 text-gray-500 border border-dashed border-gray-300 whitespace-nowrap">
                                  מצב לא דווח
                                </span>
                              )}
                              <span
                                className={`text-[11px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                                  sub.version != null
                                    ? "bg-violet-50 text-violet-700 border border-violet-200"
                                    : "bg-gray-50 text-gray-500 border border-dashed border-gray-300"
                                }`}
                              >
                                {sub.version != null ? `גרסה ${sub.version}` : "גרסה לא דווחה"}
                              </span>
                              {sourceLabel && (
                                <span className="text-[11px] text-gray-500 whitespace-nowrap">מקור: {sourceLabel}</span>
                              )}
                              {sub.evidenceCount != null && (
                                <span className="text-[11px] text-gray-500 whitespace-nowrap">{sub.evidenceCount} ראיות</span>
                              )}
                              {sub.since && (
                                <span className="text-[11px] text-gray-400 whitespace-nowrap">במצב זה מ-{fmt(sub.since)}</span>
                              )}
                              <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">
                                {sub.lastEvaluatedAt ? `נמדד: ${fmt(sub.lastEvaluatedAt)}` : "מועד הערכה לא דווח"}
                              </span>
                            </div>

                            {sub.state === "regression" && (
                              <p className="text-[11px] text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1">
                                נסיגה — המצב ירד לעומת המצב הקודם{sub.since ? ` (מאז ${fmt(sub.since)})` : ""}; הרשומה
                                ביומן שמתחת מסבירה מה גרם לירידה. ההיסטוריה נשמרת במלואה.
                              </p>
                            )}
                            {sub.state === "suspended" && (
                              <p className="text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded px-2 py-1">
                                מושהה — הידע נשמר אך אינו בשימוש עד להפעלה מחדש{sub.since ? ` (מאז ${fmt(sub.since)})` : ""}.
                                ההשהיה אינה נסיגת מיומנות ואינה מוחקת היסטוריה.
                              </p>
                            )}

                            {sub.changes.length === 0 ? (
                              <p className="text-[11px] text-gray-400">טרם תועדו שינויים בתת-תחום זה.</p>
                            ) : (
                              <>
                                <ul className="space-y-1.5 border-t border-gray-100 pt-1.5">
                                  {shown.map((change, k) => (
                                    <SkillChangeEntry key={`v${change.version ?? "?"}-${change.at || "na"}-${k}`} change={change} />
                                  ))}
                                </ul>
                                <p className="text-[11px] text-gray-400">
                                  {sub.version != null && sub.version > sub.changes.length
                                    ? `היומן המלא כולל ${sub.version} שינויים; מוצגים ${shown.length} השינויים האחרונים מתוך ${sub.changes.length} הרשומות שנטענו.`
                                    : `מוצגים ${shown.length}${sub.changes.length > shown.length ? ` מתוך ${sub.changes.length}` : ""} השינויים המתועדים (החדש ראשון).`}
                                </p>
                              </>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {data.changes && data.changes.length >= SKILL_EVALUATED_WINDOW && (
            <p className="text-[11px] text-gray-400">
              חלון השינויים הרוחבי מוגבל ל-{SKILL_EVALUATED_WINDOW} השינויים האחרונים של כל התחומים יחד.
            </p>
          )}
        </>
      )}
    </section>
  );
}

export default function LearningTimeline({ entries = [], isLoading = false, isError = false, agentId = null }) {
  const [filter, setFilter] = useState("all");
  const all = Array.isArray(entries) ? entries : [];
  const visible = all.filter((entry) => matchesFilter(entry, filter));
  const countFor = (key) => all.filter((entry) => matchesFilter(entry, key)).length;

  return (
    <section aria-label="ציר הלמידה" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-700">ציר הזמן המאוחד</h2>
        <span className="text-[11px] text-gray-400">
          {all.length > 0 ? `${all.length} רשומות מראיות מתועדות` : ""}
        </span>
      </div>
      <p className="text-[11px] text-gray-400">
        מאומת = עבודה שבוצעה ואומתה על ידי טכנאי · טרם אומת = הסתיימה בלי אימות טכנאי · לקחים ומשוב
        מוצגים בתוך העבודה שאליה הם שייכים · שינויי מצב מיומנות מוצגים עם הגרסה, הסיבה והראיה שלהם
      </p>

      <div className="flex flex-wrap gap-1.5" role="group" aria-label="סינון ציר הלמידה">
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
            className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
              filter === key
                ? "bg-brand-600 text-white border-brand-600"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {label} ({countFor(key)})
          </button>
        ))}
      </div>

      {isError && (
        <p role="alert" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
          חלק ממקורות הלמידה לא נטענו — הרשומות המוצגות חלקיות
        </p>
      )}
      {isLoading && all.length === 0 && <p className="text-sm text-gray-400 italic">טוען את ציר הלמידה...</p>}
      {!isLoading && all.length === 0 && (
        <p className="text-sm text-gray-400 italic">אין עדיין רשומות למידה לתיעוד</p>
      )}
      {!isLoading && all.length > 0 && visible.length === 0 && (
        <p className="text-sm text-gray-400 italic">אין פריטים בקטגוריה זו</p>
      )}

      <ol className="space-y-2">
        {visible.map((entry) => {
          if (entry.category === "knowledge") return <KnowledgeEntry key={entry.id} entry={entry} />;
          if (entry.category === "change") return <ChangeEntry key={entry.id} entry={entry} agentId={agentId} />;
          if (entry.category === "skill") return <SkillChangeEntry key={entry.id} change={entry} />;
          return <OutcomeEntry key={entry.id} entry={entry} />;
        })}
      </ol>
    </section>
  );
}
